/**
 * Highlight Text Utility
 *
 * Segments message text into parts based on issue spans,
 * creating an array of TextSegments for rendering.
 */

import type { InlineIssue, TextSegment, RevisedSegment } from '../types';

// Enable for debugging full message rendering issues
const DEBUG_HIGHLIGHT = __DEV__ && false; // Set to true to enable logging

/**
 * Split message text into segments based on issue positions
 *
 * @param message - Original message text
 * @param issues - Array of inline issues with start/end indices
 * @returns Array of text segments with issue references
 *
 * @example
 * // Input: "Hello world, this is fine."
 * // Issues: [{ startIndex: 6, endIndex: 11, ... }] (highlights "world")
 * // Output:
 * // [
 * //   { text: "Hello ", isIssue: false },
 * //   { text: "world", isIssue: true, issue: {...}, issueNumber: 1 },
 * //   { text: ", this is fine.", isIssue: false }
 * // ]
 */
export function highlightText(
  message: string,
  issues: InlineIssue[]
): TextSegment[] {
  if (!issues.length) {
    return [{ text: message, isIssue: false }];
  }

  // Sort issues by startIndex (should already be sorted, but ensure)
  const sortedIssues = [...issues].sort((a, b) => a.startIndex - b.startIndex);

  const segments: TextSegment[] = [];
  let currentIndex = 0;
  let issueNumber = 1;

  for (const issue of sortedIssues) {
    // Validate indices
    if (
      issue.startIndex < 0 ||
      issue.endIndex > message.length ||
      issue.startIndex >= issue.endIndex
    ) {
      console.warn('Invalid issue indices:', issue);
      continue;
    }

    // Skip if this issue overlaps with previous segment
    if (issue.startIndex < currentIndex) {
      console.warn('Overlapping issue detected:', issue);
      continue;
    }

    // Add text before this issue
    if (issue.startIndex > currentIndex) {
      segments.push({
        text: message.slice(currentIndex, issue.startIndex),
        isIssue: false,
      });
    }

    // Add the issue highlight
    segments.push({
      text: message.slice(issue.startIndex, issue.endIndex),
      isIssue: true,
      issue,
      issueNumber: issueNumber++,
    });

    currentIndex = issue.endIndex;
  }

  // Add remaining text after last issue
  if (currentIndex < message.length) {
    segments.push({
      text: message.slice(currentIndex),
      isIssue: false,
    });
  }

  // Debug output for diagnosing full message rendering
  if (DEBUG_HIGHLIGHT) {
    console.log('=== highlightText debug ===');
    console.log('Message length:', message.length);
    console.log('Issues:', issues.map((i) => ({
      id: i.id,
      start: i.startIndex,
      end: i.endIndex,
      snippet: i.snippet?.substring(0, 30),
    })));
    console.log('Segments:', segments.map((s) => ({
      text: s.text.substring(0, 40) + (s.text.length > 40 ? '...' : ''),
      isIssue: s.isIssue,
      issueNumber: s.issueNumber,
    })));
    console.log('Total segments:', segments.length);
    console.log('Issue segments:', segments.filter((s) => s.isIssue).length);
    console.log('Text segments:', segments.filter((s) => !s.isIssue).length);
  }

  return segments;
}

/**
 * Get revised text by applying accepted issue replacements
 *
 * @param message - Original message text
 * @param issues - Array of inline issues
 * @returns Revised message with accepted replacements applied
 */
export function getRevisedText(
  message: string,
  issues: InlineIssue[]
): string {
  // Sort by startIndex in reverse order to apply replacements from end
  const acceptedIssues = issues
    .filter((issue) => issue.status === 'accepted' && issue.replacement)
    .sort((a, b) => b.startIndex - a.startIndex);

  let revisedText = message;

  for (const issue of acceptedIssues) {
    revisedText =
      revisedText.slice(0, issue.startIndex) +
      issue.replacement +
      revisedText.slice(issue.endIndex);
  }

  return revisedText;
}

/**
 * Segment revised text highlighting accepted replacements with green highlights
 *
 * This function takes the ORIGINAL message and the list of issues,
 * then creates segments that show where replacements were made.
 * Unlike highlightText() which highlights problems, this highlights SOLUTIONS.
 *
 * @param originalMessage - Original message text (before any replacements)
 * @param revisedText - Message with accepted replacements applied
 * @param issues - Array of inline issues with status info
 * @returns Array of revised segments with replacement flags for green highlighting
 *
 * @example
 * // Input: original = "You ALWAYS do this!"
 * //        revised = "I've noticed this happens sometimes."
 * //        issue = { status: 'accepted', startIndex: 0, endIndex: 20, replacement: "I've noticed..." }
 * // Output:
 * // [
 * //   { text: "I've noticed this happens sometimes.", isReplacement: true, originalText: "You ALWAYS do this!" }
 * // ]
 */
export function highlightRevisedText(
  originalMessage: string,
  revisedText: string,
  issues: InlineIssue[]
): RevisedSegment[] {
  // Filter to accepted issues with replacements, sort by original position
  const acceptedIssues = issues
    .filter((issue) => issue.status === 'accepted' && issue.replacement)
    .sort((a, b) => a.startIndex - b.startIndex);

  // If no accepted issues, return plain text
  if (acceptedIssues.length === 0) {
    return [{ text: revisedText, isReplacement: false }];
  }

  const segments: RevisedSegment[] = [];
  let revisedIndex = 0; // Current position in revised text
  let offsetAdjustment = 0; // Cumulative offset due to length changes

  for (const issue of acceptedIssues) {
    const originalLength = issue.endIndex - issue.startIndex;
    const replacementLength = issue.replacement.length;

    // Calculate position in revised text (accounting for previous length changes)
    const revisedStart = issue.startIndex + offsetAdjustment;

    // Add text before this replacement
    if (revisedStart > revisedIndex) {
      segments.push({
        text: revisedText.slice(revisedIndex, revisedStart),
        isReplacement: false,
      });
    }

    // Add the replacement segment with green highlight
    const replacementEnd = revisedStart + replacementLength;
    segments.push({
      text: revisedText.slice(revisedStart, replacementEnd),
      isReplacement: true,
      originalText: issue.snippet,
      issueId: issue.id,
    });

    // Move to after this replacement
    revisedIndex = replacementEnd;

    // Update offset for next iteration
    offsetAdjustment += replacementLength - originalLength;
  }

  // Add remaining text after last replacement
  if (revisedIndex < revisedText.length) {
    segments.push({
      text: revisedText.slice(revisedIndex),
      isReplacement: false,
    });
  }

  // Debug output
  if (DEBUG_HIGHLIGHT) {
    console.log('=== highlightRevisedText debug ===');
    console.log('Original length:', originalMessage.length);
    console.log('Revised length:', revisedText.length);
    console.log('Accepted issues:', acceptedIssues.length);
    console.log('Segments:', segments.map((s) => ({
      text: s.text.substring(0, 40) + (s.text.length > 40 ? '...' : ''),
      isReplacement: s.isReplacement,
    })));
  }

  return segments;
}

/**
 * Check if any issues have been accepted or ignored
 */
export function hasChanges(issues: InlineIssue[]): boolean {
  return issues.some((issue) => issue.status !== 'pending');
}

/**
 * Count issues by status
 */
export function countByStatus(
  issues: InlineIssue[]
): Record<'pending' | 'accepted' | 'ignored', number> {
  return issues.reduce(
    (acc, issue) => {
      acc[issue.status]++;
      return acc;
    },
    { pending: 0, accepted: 0, ignored: 0 }
  );
}

export default highlightText;
